import React, { useEffect, useRef, useState } from 'react';
import { Download, Share2, X, Copy, Check } from 'lucide-react';

/**
 * LeaderboardShareCard - Mundalo branded shareable PNG.
 *
 *   "You're #3 in CDMX this week - 210 pts. Move abroad. Live freely. mundalo.com"
 *
 * • Renders to a 1080×1350 HTML5 Canvas (Instagram Stories ratio 4:5).
 * • Provides Download, Web Share (native share sheet → IG / X / Messages),
 *   and Copy-link fallback for desktop where Web Share isn't supported.
 * • Used in /portal/community/leaderboard via the "Share my rank" button.
 *
 * Brand tokens:
 *   • Bg:        Canvas      #F9F6EF
 *   • Accent:    Golden Hour #E8A830
 *   • Heading:   Cormorant Garamond, italic
 *   • Body:      Outfit (sans-serif)
 *   • Sub-mark:  Pacific     #1A5C6E
 */
const W = 1080;
const H = 1350;

const LeaderboardShareCard = ({ open, onClose, rank, points, cityName, firstName, windowLabel }) => {
  const canvasRef = useRef(null);
  const [shareSupported] = useState(
    typeof navigator !== 'undefined' && typeof navigator.share === 'function',
  );
  const [copied, setCopied] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);

  const scope = cityName ? `in ${cityName}` : 'on Mundalo';
  // Default to "this week" but honour the actual time-window the viewer is on.
  const windowText = windowLabel || 'this week';
  const name = firstName || 'You';

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    c.width = W;
    c.height = H;

    // Background - Mundalo Canvas
    ctx.fillStyle = '#F9F6EF';
    ctx.fillRect(0, 0, W, H);

    // Subtle grain - Pacific dots top-right
    ctx.fillStyle = 'rgba(26,92,110,0.06)';
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      ctx.fillRect(x, y, 2, 2);
    }

    // Top Mundalo wordmark
    ctx.fillStyle = '#0A1F2E';
    ctx.font = 'italic 64px "Cormorant Garamond", serif';
    ctx.textAlign = 'center';
    ctx.fillText('Mundalo', W / 2, 130);

    ctx.fillStyle = '#1A5C6E';
    ctx.font = '600 22px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('MOVE ABROAD · LIVE FREELY', W / 2, 175);

    // Big rank - Golden Hour
    ctx.fillStyle = '#E8A830';
    ctx.font = '800 380px "Outfit", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`#${rank}`, W / 2, 580);

    // Body line - "in CDMX this week"
    ctx.fillStyle = '#0A1F2E';
    ctx.font = 'italic 56px "Cormorant Garamond", serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${name} ${scope} ${windowText}.`, W / 2, 700);

    // Divider
    ctx.strokeStyle = '#C4B9A0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 90, 770);
    ctx.lineTo(W / 2 + 90, 770);
    ctx.stroke();

    // Points pill
    ctx.fillStyle = '#1A5C6E';
    ctx.font = '600 28px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('POINTS', W / 2, 840);

    ctx.fillStyle = '#0A1F2E';
    ctx.font = '700 180px "Outfit", sans-serif';
    ctx.fillText(`${points}`, W / 2, 1010);

    // CTA line at the bottom
    ctx.fillStyle = '#0A1F2E';
    ctx.font = '400 32px "Outfit", sans-serif';
    ctx.fillText('Plan your move abroad.', W / 2, 1180);

    ctx.fillStyle = '#1A5C6E';
    ctx.font = '700 36px "Outfit", sans-serif';
    ctx.fillText('mundalo.com', W / 2, 1240);

    // Golden Hour bottom accent bar
    ctx.fillStyle = '#E8A830';
    ctx.fillRect(0, H - 12, W, 12);

    setDataUrl(c.toDataURL('image/png'));
  }, [open, rank, points, name, scope, windowText]);

  if (!open) return null;

  const filename = `mundalo-rank-${rank}-${(cityName || 'global').toLowerCase().replace(/\s+/g, '-')}.png`;

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const share = async () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], filename, { type: 'image/png' });
      const shareData = {
        title: 'My Mundalo rank',
        text: `${name} ${scope} ${windowText} - ${points} pts. mundalo.com`,
        files: [file],
      };
      try {
        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
        } else {
          await navigator.share({
            title: shareData.title,
            text: shareData.text,
            url: 'https://mundalo.com',
          });
        }
      } catch {
        // user dismissed - no-op
      }
    }, 'image/png');
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(
        `${name} ${scope} ${windowText} - ranked #${rank} with ${points} pts on Mundalo. Plan your move abroad → mundalo.com`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // browser blocked clipboard - silent
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/65 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      data-testid="share-card-modal"
    >
      <div className="bg-[#F9F6EF] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-[#C4B9A0] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#EFE6CF]">
          <h2 className="font-display italic text-xl text-[#0A1F2E]">Share your rank</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-[#5A6B73] hover:text-[#0A1F2E]"
            data-testid="share-card-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div className="bg-[#0A1F2E]/4 rounded-lg overflow-hidden mb-4 border border-[#EFE6CF]">
            {/* Render canvas at 1/3 scale via CSS for preview */}
            <canvas
              ref={canvasRef}
              className="w-full h-auto block"
              style={{ aspectRatio: `${W}/${H}` }}
              data-testid="share-card-canvas"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={download}
              className="flex flex-col items-center gap-1 py-3 rounded-lg bg-[#E8A830] text-[#0A1F2E] font-bold text-xs uppercase tracking-wider transition hover:brightness-95"
              data-testid="share-card-download"
            >
              <Download className="w-4 h-4" /> Download
            </button>
            <button
              type="button"
              onClick={share}
              disabled={!shareSupported}
              className="flex flex-col items-center gap-1 py-3 rounded-lg bg-[#0A1F2E] text-white font-semibold text-xs uppercase tracking-wider transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
              title={shareSupported ? 'Share to apps' : 'Share not supported on this browser - use Download'}
              data-testid="share-card-native-share"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              type="button"
              onClick={copyText}
              className="flex flex-col items-center gap-1 py-3 rounded-lg bg-white border border-[#C4B9A0] text-[#0A1F2E] font-semibold text-xs uppercase tracking-wider transition hover:bg-[#F9F6EF]"
              data-testid="share-card-copy"
            >
              {copied ? <Check className="w-4 h-4 text-[#1A5C6E]" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-[11px] text-[#5A6B73] text-center mt-3 leading-relaxed">
            Share to Instagram Stories, X, or anywhere else.
            {!shareSupported && ' Native share unsupported on this browser - use Download then post manually.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardShareCard;
